import { pickedFile } from '../../../utils/pickedFile.js'
import MediaCaptureField from '../../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react'
import SelectField from '../../common/SelectField.js';
import Alert from '../../common/Alert'
import vehicleContractPaymentService from '../../../services/vehicleContractPaymentService'
import { X } from 'lucide-react'
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js'
import { useTranslation } from 'react-i18next';


const EditVehicleContractPaymentModal = ({ paymentId, onSuccess, onClose }) => {
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: '',
    payment_mode: '',
    reference_number: '',
    remarks: '',
    receipt_image: null
  })
  const { errors, validate, validateField } = useValidation()
  const [previewImage, setPreviewImage] = useState(null)
  const [existingImage, setExistingImage] = useState(false)

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
  }

  useEffect(() => {
    fetchPayment()
  }, [paymentId])

  const fetchPayment = async () => {
    try {
      setLoading(true)
      const response = await vehicleContractPaymentService.getOne(paymentId)
      const payment = response.data.data

      setFormData({
        amount: payment.amount,
        payment_date: payment.payment_date,
        payment_mode: payment.payment_mode || '',
        reference_number: payment.reference_number || '',
        remarks: payment.remarks || '',
        receipt_image: null
      })

      if (payment.has_receipt_image) {
        setExistingImage(true)
      }
    } catch (err) {
      Alert.error('Failed to load payment details')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    let filtered = value
    if (name === 'amount') filtered = inputFilters.amount(value)

    setFormData(prev => ({ ...prev, [name]: filtered }))

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleImageChange = (e) => {
    const file = pickedFile(e)
    if (file) {
      setFormData(prev => ({
        ...prev,
        receipt_image: file
      }))

      // Preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviewImage(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting')
      return
    }

    try {
      setSubmitting(true)
      const formDataToSend = new FormData()
      formDataToSend.append('amount', parseFloat(formData.amount))
      formDataToSend.append('payment_date', formData.payment_date)
      if (formData.payment_mode) formDataToSend.append('payment_mode', formData.payment_mode)
      if (formData.reference_number) formDataToSend.append('reference_number', formData.reference_number)
      if (formData.remarks) formDataToSend.append('remarks', formData.remarks)
      if (formData.receipt_image) formDataToSend.append('receipt_image', formData.receipt_image)

      await vehicleContractPaymentService.update(paymentId, formDataToSend)

      Alert.success('Payment updated successfully!')
      onSuccess()
      onClose()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update payment')
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-secondary">Edit Payment</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.amount ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.amount} />
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.payment_date ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.payment_date} />
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Mode
              </label>
              <SelectField variant="form"
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              >
                <option value="">Select payment mode</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="other">Other</option>
              </SelectField>
            </div>

            {/* Reference Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference Number
              </label>
              <input
                type="text"
                name="reference_number"
                value={formData.reference_number}
                onChange={handleChange}
                placeholder="Cheque/Transfer reference"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>

            {/* Receipt Image */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receipt Image
              </label>
              <MediaCaptureField mode="photo" onPick={handleImageChange} />
              {existingImage && !previewImage && (
                <p className="text-sm text-gray-500 mt-2">Receipt image already attached</p>
              )}
              {previewImage && (
                <div className="mt-4">
                  <img
                    src={previewImage}
                    alt="Receipt preview"
                    className="max-w-xs max-h-40 rounded-lg border border-gray-200"
                  />
                </div>
              )}
            </div>

            {/* Remarks */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Enter any additional remarks"
                rows="3"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>

          {/* Modal Actions */}
          <div className="mt-8 flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition"
            >
              {submitting ? 'Updating...' : 'Update Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditVehicleContractPaymentModal
